import { z } from 'zod';
import { BusinessDateSchema, IdSchema } from './encoding.js';
/**
 * Project work management contracts (CLR-66, 29 October 2026; FRD-PRJ): the work of a project —
 * its members and their project roles, boards and their columns, milestones, labels, tasks with
 * one level of subtasks and checklists, comments with @mentions and the activity feed — and the
 * caller's own assigned tasks for the dashboard. Employees only.
 */
const DateTimeSchema = z.iso.datetime({ offset: true });
/** A member's role in one project. Data on the membership, never an application role. */
export const ProjectRoleSchema = z.enum(['manager', 'member', 'viewer']);
/** What a column means for the work in it, whatever it is called. */
export const ColumnCategorySchema = z.enum(['todo', 'in_progress', 'review', 'done']);
export const TaskPrioritySchema = z.enum(['low', 'medium', 'high', 'urgent']);
const ColourSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'must be a colour like #2f8f6b');
/** A project the caller may open the work of, with their role there (null: the project office). */
export const WorkProjectSchema = z.object({
    id: IdSchema,
    code: z.string(),
    name: z.string(),
    status: z.enum(['active', 'inactive']),
    myRole: ProjectRoleSchema.nullable(),
});
export const ProjectMemberSchema = z.object({
    id: IdSchema,
    employeeId: IdSchema,
    name: z.string(),
    /** The user who signs in as the employee, for their photo; null when none. */
    userId: IdSchema.nullable(),
    role: ProjectRoleSchema,
});
export const BoardColumnSchema = z.object({
    id: IdSchema,
    name: z.string(),
    category: ColumnCategorySchema,
    sequence: z.number().int(),
});
export const BoardSchema = z.object({
    id: IdSchema,
    name: z.string(),
    status: z.enum(['active', 'archived']),
    sequence: z.number().int(),
    columns: z.array(BoardColumnSchema),
    version: z.number().int(),
});
export const MilestoneSchema = z.object({
    id: IdSchema,
    name: z.string(),
    description: z.string().nullable(),
    dueDate: z.string().nullable(),
    status: z.enum(['open', 'completed']),
    version: z.number().int(),
});
export const LabelSchema = z.object({
    id: IdSchema,
    name: z.string(),
    colour: z.string(),
});
/** Everything the project's work pages need to start: one request. */
export const ProjectWorkspaceSchema = z.object({
    project: z.object({
        id: IdSchema,
        code: z.string(),
        name: z.string(),
        status: z.enum(['active', 'inactive']),
    }),
    /** The caller's role here; null for the project office, who is not a member. */
    myRole: ProjectRoleSchema.nullable(),
    /** Members, boards, columns, milestones and labels may be changed by the caller. */
    canManage: z.boolean(),
    /** Tasks may be created, changed and moved by the caller. */
    canEdit: z.boolean(),
    members: z.array(ProjectMemberSchema),
    boards: z.array(BoardSchema),
    milestones: z.array(MilestoneSchema),
    labels: z.array(LabelSchema),
});
export const MemberCandidateSchema = z.object({ employeeId: IdSchema, name: z.string() });
export const AddMemberRequestSchema = z.strictObject({
    employeeId: IdSchema,
    role: ProjectRoleSchema,
});
export const ChangeMemberRequestSchema = z.strictObject({ role: ProjectRoleSchema });
export const CreateBoardRequestSchema = z.strictObject({
    name: z.string().trim().min(1).max(100),
});
export const UpdateBoardRequestSchema = z.strictObject({
    name: z.string().trim().min(1).max(100).optional(),
    status: z.enum(['active', 'archived']).optional(),
});
export const CreateColumnRequestSchema = z.strictObject({
    name: z.string().trim().min(1).max(60),
    category: ColumnCategorySchema,
});
export const UpdateColumnRequestSchema = z.strictObject({
    name: z.string().trim().min(1).max(60).optional(),
    category: ColumnCategorySchema.optional(),
    /** The column's new place on the board, from 1. */
    sequence: z.number().int().min(1).optional(),
});
export const CreateMilestoneRequestSchema = z.strictObject({
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().min(1).max(4000).optional(),
    dueDate: BusinessDateSchema.optional(),
});
export const UpdateMilestoneRequestSchema = z.strictObject({
    name: z.string().trim().min(1).max(200).optional(),
    description: z.string().trim().min(1).max(4000).nullable().optional(),
    dueDate: BusinessDateSchema.nullable().optional(),
    status: z.enum(['open', 'completed']).optional(),
});
export const CreateLabelRequestSchema = z.strictObject({
    name: z.string().trim().min(1).max(40),
    colour: ColourSchema,
});
export const UpdateLabelRequestSchema = z.strictObject({
    name: z.string().trim().min(1).max(40).optional(),
    colour: ColourSchema.optional(),
});
/** A task as lists, boards and calendars show it. */
export const TaskSummarySchema = z.object({
    id: IdSchema,
    projectId: IdSchema,
    /** The project code and the task's number in it, e.g. `IBMS-12`. */
    key: z.string(),
    boardId: IdSchema,
    columnId: IdSchema,
    /** The column's category: `done` means the task is complete. */
    category: ColumnCategorySchema,
    parentTaskId: IdSchema.nullable(),
    milestoneId: IdSchema.nullable(),
    title: z.string(),
    priority: TaskPrioritySchema,
    startDate: z.string().nullable(),
    dueDate: z.string().nullable(),
    estimateMinutes: z.number().int().nullable(),
    position: z.number(),
    assigneeIds: z.array(IdSchema),
    labelIds: z.array(IdSchema),
    checklist: z.object({ done: z.number().int(), total: z.number().int() }),
    subtasks: z.object({ done: z.number().int(), total: z.number().int() }),
    comments: z.number().int(),
    completedAt: DateTimeSchema.nullable(),
    archived: z.boolean(),
    version: z.number().int(),
});
export const ChecklistItemSchema = z.object({
    id: IdSchema,
    text: z.string(),
    done: z.boolean(),
    sequence: z.number().int(),
});
export const TaskDetailSchema = TaskSummarySchema.extend({
    description: z.string().nullable(),
    checklistItems: z.array(ChecklistItemSchema),
    createdAt: DateTimeSchema,
    createdByName: z.string().nullable(),
});
const TaskTitle = z.string().trim().min(1).max(300);
const TaskDescription = z.string().trim().min(1).max(20000);
const Estimate = z
    .number()
    .int()
    .min(0)
    .max(60 * 24 * 365);
export const CreateTaskRequestSchema = z.strictObject({
    boardId: IdSchema,
    columnId: IdSchema,
    /** Makes the task a subtask of a top-level task on the same board. */
    parentTaskId: IdSchema.optional(),
    milestoneId: IdSchema.optional(),
    title: TaskTitle,
    description: TaskDescription.optional(),
    priority: TaskPrioritySchema.optional(),
    startDate: BusinessDateSchema.optional(),
    dueDate: BusinessDateSchema.optional(),
    estimateMinutes: Estimate.optional(),
    assigneeIds: z.array(IdSchema).max(20).optional(),
    labelIds: z.array(IdSchema).max(20).optional(),
});
/** A partial change; `null` clears. Lists replace the task's whole list. */
export const UpdateTaskRequestSchema = z.strictObject({
    title: TaskTitle.optional(),
    description: TaskDescription.nullable().optional(),
    priority: TaskPrioritySchema.optional(),
    milestoneId: IdSchema.nullable().optional(),
    startDate: BusinessDateSchema.nullable().optional(),
    dueDate: BusinessDateSchema.nullable().optional(),
    estimateMinutes: Estimate.nullable().optional(),
    assigneeIds: z.array(IdSchema).max(20).optional(),
    labelIds: z.array(IdSchema).max(20).optional(),
    archived: z.boolean().optional(),
});
/** Moves a task to a column, before another task there, or to its end. */
export const MoveTaskRequestSchema = z.strictObject({
    columnId: IdSchema,
    beforeTaskId: IdSchema.nullable(),
});
export const CreateChecklistItemRequestSchema = z.strictObject({
    text: z.string().trim().min(1).max(300),
});
export const UpdateChecklistItemRequestSchema = z.strictObject({
    text: z.string().trim().min(1).max(300).optional(),
    done: z.boolean().optional(),
});
export const TaskCommentSchema = z.object({
    id: IdSchema,
    taskId: IdSchema,
    authorUserId: IdSchema,
    authorName: z.string(),
    body: z.string(),
    mentionEmployeeIds: z.array(IdSchema),
    createdAt: DateTimeSchema,
    editedAt: DateTimeSchema.nullable(),
});
export const CreateCommentRequestSchema = z.strictObject({
    body: z.string().trim().min(1).max(10000),
    /** Project members to notify; each must be a member of the project. */
    mentionEmployeeIds: z.array(IdSchema).max(20).optional(),
});
export const UpdateCommentRequestSchema = z.strictObject({
    body: z.string().trim().min(1).max(10000),
});
/** One entry of a project's activity feed. `detail` is what changed, by kind. */
export const ActivityEntrySchema = z.object({
    id: IdSchema,
    taskId: IdSchema.nullable(),
    taskKey: z.string().nullable(),
    actorName: z.string().nullable(),
    kind: z.string(),
    summary: z.string(),
    occurredAt: DateTimeSchema,
});
/** One of the caller's open assigned tasks, for My tasks and the daily dashboard popup. */
export const MyTaskSchema = z.object({
    id: IdSchema,
    key: z.string(),
    title: z.string(),
    projectId: IdSchema,
    projectName: z.string(),
    columnName: z.string(),
    category: ColumnCategorySchema,
    priority: TaskPrioritySchema,
    startDate: z.string().nullable(),
    dueDate: z.string().nullable(),
    estimateMinutes: z.number().int().nullable(),
    checklist: z.object({ done: z.number().int(), total: z.number().int() }),
});
//# sourceMappingURL=project-work.js.map